import { BRIEF_DRAFTING_QUERY_KEY } from "@entities/brief/lib/constants";
import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  IDeleteDraftingComment,
  IDraftingResponse,
} from "@shared/lib/types/drafting";
import { useQueryClient } from "@tanstack/react-query";
import { BriefService } from "../../services/brief.service";

export function useDeleteDraftingComment(draftingId: string) {
  const queryClient = useQueryClient();

  const removeComment = (payload: IDeleteDraftingComment) => {
    queryClient.setQueryData(
      BRIEF_DRAFTING_QUERY_KEY(draftingId),
      (oldData: BackednResponse<IDraftingResponse>) => {
        return {
          ...oldData,
          data: {
            ...oldData.data,
            drafting: {
              ...oldData.data.drafting,
              draftingContents: oldData.data?.drafting?.draftingContents?.map(
                (content) => {
                  if (content._id === payload.draftingVersionId) {
                    return {
                      ...content,
                      comments: (content.comments || []).filter(
                        (comment) => comment._id !== payload.commentId
                      ),
                    };
                  }
                  return content;
                }
              ),
            },
          },
        };
      }
    );
  };

  const {
    mutate: deleteComment,
    isLoading: isDeleting,
    isSuccess: isDeleted,
    variables,
  } = useMutation({
    mutationFn: async (payload: IDeleteDraftingComment) => {
      // Optimistically remove
      removeComment(payload);
      return BriefService.deleteDraftingComment(payload).catch(async (err) => {
        const responseBody = await err.response?.json?.();
        if (responseBody?.msg) ToastService.error(responseBody.msg);
        // Revert on error - we'd need to restore the comment, but for now just invalidate
        queryClient.invalidateQueries({
          queryKey: BRIEF_DRAFTING_QUERY_KEY(draftingId),
        });
        throw err;
      });
    },
    options: {
      skipInvalidation: true,
    },
    onSuccess: () => {
      // Comment already removed optimistically
    },
  });

  return {
    deleteComment,
    isDeleting,
    isDeleted,
    deleteCommentId: variables?.commentId || "",
  };
}
